/**
 * Dense form controls for the editor's side panels (the page-sized inputs are
 * 40 px tall — too big for property panels). Text and number fields commit
 * on Enter/blur and revert on Escape, so every commit is one undo step.
 */

import { ChevronDownIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useState } from 'react';
import type { Vec2 } from 'shared-types';
import { isTurnedView } from '../canvas/viewport';
import { useEditorState, useObjectEditor } from '../EditorContext';
import { NumberStepArrows } from '../../../components/inputs/Input/NumberStepArrows';
import { SwitchToggle } from '../../../components/SwitchToggle';
import { Tooltip } from '../../../components/Tooltip';

const inputClass = 'h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-sm text-black outline-none focus:border-french-violet-600 dark:border-gray-600 dark:bg-[#262626] dark:text-white dark:focus:border-neon-green-300';

export const PanelSection: React.FC2<{ title: React.ReactNode; actions?: React.ReactNode; collapsible?: boolean }> = ({ title, actions, collapsible = false, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <section className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
      <div className="mb-2 flex items-center gap-2">
        <button type="button" disabled={!collapsible} onClick={() => setOpen((o) => !o)} className="flex flex-1 items-center gap-1 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {collapsible && <ChevronDownIcon className={classNames('h-3 w-3 transition-transform', !open && '-rotate-90')} />}
          {title}
        </button>
        {actions}
      </div>
      {open && <div className="flex flex-col gap-2">{children}</div>}
    </section>
  );
};

/**
 * A label that explains itself on hover: the `hint` sentence is a tooltip,
 * not text under the field.
 */
export const HintLabel: React.FC2<{ hint?: React.ReactNode; className?: string }> = ({ hint, className, children }) =>
  hint ? (
    <Tooltip message={<span className="block max-w-64 text-xs">{hint}</span>} delay={300} wrapperClassName={classNames('min-w-0', className)}>
      <span className="block truncate">{children}</span>
    </Tooltip>
  ) : (
    <span className={classNames('truncate', className)} title={typeof children === 'string' ? children : undefined}>
      {children}
    </span>
  );

export const FieldRow: React.FC2<{ label: React.ReactNode; hint?: React.ReactNode }> = ({ label, hint, children }) => (
  <label className="grid grid-cols-[7rem_1fr] items-center gap-2 text-sm">
    <HintLabel hint={hint} className="text-gray-600 dark:text-gray-300">
      {label}
    </HintLabel>
    <span className="min-w-0">{children}</span>
  </label>
);

/** A row whose fields need the full width (a pair of numbers in a narrow card): the label (and an action) above them. */
export const StackedFieldRow: React.FC2<{ label: React.ReactNode; hint?: React.ReactNode; action?: React.ReactNode }> = ({ label, hint, action, children }) => (
  <div className="flex flex-col gap-1 text-sm">
    <div className="flex min-h-6 items-center justify-between gap-2">
      <HintLabel hint={hint} className="text-gray-600 dark:text-gray-300">
        {label}
      </HintLabel>
      {action}
    </div>
    {children}
  </div>
);

interface NumberFieldProps {
  value: number;
  onCommit: (value: number) => void;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  decimals?: number;
  integer?: boolean;
  disabled?: boolean;
  /**
   * Live mode: every valid value while typing or scrubbing (the caller previews it); the edit is framed by
   * onEditStart/onEditEnd. Without it the field is live on the draft: each value goes to onCommit inside one store
   * transaction (one undo step per edit, Escape puts it back).
   */
  onLiveChange?: (value: number) => void;
  onEditStart?: () => void;
  /** Blur/Enter: after the final onCommit. */
  onEditEnd?: () => void;
  /** Escape: the caller drops its preview. */
  onEditCancel?: () => void;
}

const NumberField_: React.FC<NumberFieldProps> = ({ value, onCommit, unit, min, max, step = 0.01, decimals = 3, integer, disabled, onLiveChange, onEditStart, onEditEnd, onEditCancel }) => {
  const { store } = useObjectEditor();
  // Only the text being typed is local; otherwise the field shows the document value.
  const [draft, setDraft] = useState<string | null>(null);
  /** The caller previews on its own (test mode, default size), else the draft follows every value. */
  const ownLive = onLiveChange !== undefined;
  const live = onLiveChange ?? ((next: number) => next !== value && onCommit(next));
  const shown = draft ?? (integer ? String(Math.round(value)) : String(Number(value.toFixed(decimals))));
  const commit = () => {
    if (draft === null) return;
    const parsed = Number(draft.replace(',', '.'));
    setDraft(null);
    if (!Number.isFinite(parsed)) return;
    let next = integer ? Math.round(parsed) : parsed;
    if (min !== undefined) next = Math.max(min, next);
    if (max !== undefined) next = Math.min(max, next);
    if (next !== value) onCommit(next);
  };
  return (
    <span className={classNames('number-field flex items-center', unit && 'number-field-with-unit')}>
      <input
        type="number"
        inputMode="decimal"
        disabled={disabled}
        value={shown}
        step={integer ? 1 : step}
        min={min}
        max={max}
        onChange={(e) => {
          setDraft(e.target.value);
          const parsed = Number(e.target.value.replace(',', '.'));
          const inRange = Number.isFinite(parsed) && (min === undefined || parsed >= min) && (max === undefined || parsed <= max);
          if (inRange && e.target.value.trim() !== '') live(integer ? Math.round(parsed) : parsed);
        }}
        onFocus={() => {
          if (!ownLive) store.begin();
          onEditStart?.();
        }}
        onBlur={() => {
          commit();
          if (!ownLive) store.commit();
          onEditEnd?.();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setDraft(null);
            if (!ownLive) store.rollback();
            onEditCancel?.();
            e.currentTarget.blur();
          }
        }}
        className={classNames(inputClass, 'px-5', disabled && 'opacity-50')}
      />
      {/* The unit right behind the number (see .number-field-unit in index.css): the value is drawn here, with it. */}
      {unit && (
        <span className="number-field-unit left-[21px] right-[21px] text-sm text-black dark:text-white">
          <span className="number-field-unit-value">{shown}</span>
          <span className="ml-1 text-xs text-gray-400">{unit}</span>
        </span>
      )}
      {!disabled && <NumberStepArrows />}
    </span>
  );
};
export const NumberField = memo(NumberField_);

/**
 * A point on the drawing as X / Y in meters from the object's center, X to
 * the right and Y up as the canvas shows it (the spec's u / n, negated on a
 * turned canvas), named the way drawing apps do.
 */
const PointFields_: React.FC<{ value: Vec2; onCommit: (point: Vec2) => void }> = ({ value, onCommit }) => {
  const { spec } = useEditorState();
  const sign = isTurnedView(spec) ? -1 : 1;
  return (
    <span className="grid grid-cols-2 gap-1">
      {(['X', 'Y'] as const).map((axis, i) => (
        <span key={axis} className="flex min-w-0 items-center gap-1">
          <span className="w-3 shrink-0 text-xs text-gray-500 dark:text-gray-400">{axis}</span>
          <NumberField value={sign * value[i]} unit="m" onCommit={(v) => onCommit(i === 0 ? [sign * v, value[1]] : [value[0], sign * v])} />
        </span>
      ))}
    </span>
  );
};
export const PointFields = memo(PointFields_);

const TextField_: React.FC<{ value: string; onCommit: (value: string) => void; placeholder?: string; maxLength?: number }> = ({ value, onCommit, placeholder, maxLength }) => {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    setDraft(null);
    if (draft !== value) onCommit(draft);
  };
  return (
    <input
      type="text"
      value={draft ?? value}
      placeholder={placeholder}
      maxLength={maxLength}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          setDraft(null);
          e.currentTarget.blur();
        }
      }}
      className={inputClass}
    />
  );
};
export const TextField = memo(TextField_);

/** Native select (dense). Labels are plain, already translated strings: <option> only holds text. */
export const SelectField = <V extends string>({ value, options, onChange }: { value: V; options: ReadonlyArray<{ value: V; label: string }>; onChange: (value: V) => void }) => (
  <select
    value={value}
    onChange={(e) => {
      const next = options.find((o) => o.value === e.target.value);
      if (next) onChange(next.value);
    }}
    // The forms plugin pads selects for 40 px fields: the dense field centers its text instead (and keeps room for the chevron).
    className={classNames(inputClass, 'py-0 pr-8 bg-[position:right_0.375rem_center]')}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </select>
);

/** Compact segmented choice (2–5 short options). */
export const Segmented = <V extends string>({ value, options, onChange }: { value: V | null; options: ReadonlyArray<{ value: V; label: React.ReactNode; title?: string }>; onChange: (value: V) => void }) => (
  <span role="radiogroup" className="flex overflow-hidden rounded-md border border-gray-300 dark:border-gray-600">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        role="radio"
        aria-checked={value === o.value}
        title={o.title}
        onClick={() => onChange(o.value)}
        className={classNames(
          'flex-1 px-2 py-1 text-xs transition-colors',
          value === o.value ? 'bg-neon-green-300 font-medium text-black' : 'bg-white hover:bg-gray-100 dark:bg-[#262626] dark:hover:bg-black',
        )}
      >
        {o.label}
      </button>
    ))}
  </span>
);

export const ToggleRow: React.FC<{ label: React.ReactNode; checked: boolean; onChange: (value: boolean) => void; hint?: React.ReactNode }> = ({ label, checked, onChange, hint }) => (
  <div className="flex items-center justify-between gap-2 text-sm">
    <HintLabel hint={hint} className="text-gray-600 dark:text-gray-300">
      {label}
    </HintLabel>
    <SwitchToggle switchedOn={checked} onChange={onChange} />
  </div>
);

export const PanelButton: React.FC2<{ onClick: () => void; disabled?: boolean; danger?: boolean; title?: string }> = ({ onClick, disabled, danger, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={classNames(
      'flex items-center justify-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40',
      danger ? 'border-error-red-500/50 text-error-red-500 hover:bg-error-red-500/10' : 'border-gray-300 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-black',
    )}
  >
    {children}
  </button>
);
